import { useEffect, useState } from 'react';
import { Badge, Button, Caption1, Spinner } from '@fluentui/react-components';
import type { ButtonProps } from '@fluentui/react-components';
import { BrainSparkleRegular, CheckmarkCircleRegular } from '@fluentui/react-icons';
import { ImplementationProposalDiffDialog } from '../../../change/components/ChangeAssessmentScreen';
import {
  getImplementationProposalDiffKey,
  getImplementationProposalReviewGateState,
} from '../../../change/implementationPlanViewModel';
import { TASK_STATUS } from '../../models/plan';
import type { TChangeV2, TPlanTask } from '../../models';
import { useChangeV2Store } from '../../state/ChangeV2Store';

const PROPOSAL_LOADING_MS = 4000;

type TProps = {
  change: TChangeV2;
  task: TPlanTask;
  readOnly?: boolean;
  triggerProps?: Pick<ButtonProps, 'appearance' | 'size'>;
};

export const TaskSolutionProposal = ({ change, task, readOnly, triggerProps }: TProps) => {
  const { updatePlanTask } = useChangeV2Store();
  const [loading, setLoading] = useState(false);
  const [viewedDiffIds, setViewedDiffIds] = useState<string[]>([]);
  const [selectedDiffIndex, setSelectedDiffIndex] = useState<number>();
  const proposal = task.implementationProposal;
  const diffs = proposal?.diffs ?? [];
  const canPropose = Boolean(
    proposal?.available && diffs.length && !readOnly && !task.proposalAcceptedAt && task.status !== TASK_STATUS.COMPLETE
  );

  useEffect(() => {
    if (!loading) return;
    if (!canPropose) {
      setLoading(false);
      return;
    }
    const timeout = window.setTimeout(() => {
      setLoading(false);
      setViewedDiffIds([getImplementationProposalDiffKey(diffs[0], 0)]);
      setSelectedDiffIndex(0);
    }, PROPOSAL_LOADING_MS);
    return () => window.clearTimeout(timeout);
  }, [loading, canPropose, proposal]);

  if (!proposal?.available) return null;

  const selectedDiff = selectedDiffIndex === undefined ? undefined : diffs[selectedDiffIndex];
  // Clicking Accept is the explicit consent in this direct diff review flow.
  const gate = getImplementationProposalReviewGateState({ proposal, viewedDiffIds, consentChecked: true });
  const openDiff = (index: number) => {
    const diffKey = getImplementationProposalDiffKey(diffs[index], index);
    setViewedDiffIds((ids) => (ids.includes(diffKey) ? ids : [...ids, diffKey]));
    setSelectedDiffIndex(index);
  };
  const accept = () => {
    if (!gate.canAccept || !canPropose || !selectedDiff) return;
    updatePlanTask(change.id, task.id, {
      proposalAcceptedAt: new Date().toISOString(),
      status: TASK_STATUS.IN_REVIEW,
    });
    setSelectedDiffIndex(undefined);
  };

  return (
    <div
      className="flex flex-col gap-3"
      onClick={(event) => event.stopPropagation()}
      onKeyDown={(event) => event.stopPropagation()}
    >
      {task.proposalAcceptedAt ? (
        <>
          <Badge appearance="tint" color="success" icon={<CheckmarkCircleRegular />}>
            AI solution accepted
          </Badge>
          <Caption1>The proposed document changes are ready for review.</Caption1>
        </>
      ) : canPropose ? (
        <Button
          appearance="secondary"
          {...triggerProps}
          icon={loading ? <Spinner size="tiny" /> : <BrainSparkleRegular />}
          disabled={loading}
          aria-busy={loading}
          onClick={() => setLoading(true)}
        >
          Propose solution
        </Button>
      ) : null}
      <ImplementationProposalDiffDialog
        selectedDiff={
          selectedDiff
            ? { diff: selectedDiff, diffKey: getImplementationProposalDiffKey(selectedDiff, selectedDiffIndex) }
            : undefined
        }
        onClose={() => setSelectedDiffIndex(undefined)}
        actions={
          <>
            {selectedDiffIndex !== undefined && diffs.length > 1 ? (
              <>
                <Button disabled={selectedDiffIndex === 0} onClick={() => openDiff(selectedDiffIndex - 1)}>
                  Previous diff
                </Button>
                <Caption1>
                  Diff {selectedDiffIndex + 1} of {diffs.length}
                </Caption1>
                <Button
                  disabled={selectedDiffIndex === diffs.length - 1}
                  onClick={() => openDiff(selectedDiffIndex + 1)}
                >
                  Next diff
                </Button>
              </>
            ) : null}
            <Button appearance="secondary" onClick={() => setSelectedDiffIndex(undefined)}>
              Reject
            </Button>
            <Button appearance="primary" disabled={!gate.canAccept || !canPropose} onClick={accept}>
              Accept
            </Button>
          </>
        }
      />
    </div>
  );
};
